/**
 * @file MediaElementPanels.tsx
 * @description Панели «Галерея», «Видео», «Аудио» (phase19.16 Р2;
 * uiux-design-r2 §4; SB97; решение 2026-10-09 — видео только файлом из
 * медиатеки, без площадок по ссылке): фото галереи перетаскиваются, в ряд
 * 1–6 числом с подписью «на планшете N», на телефоне схемами [стопкой][по
 * две][лентой] (SB120 этап 3б); видео — файл и заставка; звук — файл и
 * название.
 */

import { GRID_PER_ROW, SECTION_TEXT_LIMITS, SITE_LIMITS, defaultElementBody } from "@chaiberi/shared";
import { Plus } from "lucide-react";
import { useState } from "react";

import { Button } from "../../../ui/button";
import { MediaPickerDialog } from "../../../site-view/media/MediaPickerDialog";
import { MediaIdField } from "../fields/MediaIdField";
import { SectionTextField } from "../fields/SectionTextField";
import { SortableList } from "../fields/SortableList";
import { useSectionForm } from "../section-form-context";
import { contentMediaUrl } from "../../../../lib/api-content-media";
import { GridCountField } from "./GridCountField";
import { PhoneGridField } from "./PhoneGridField";
import type { ElementPanelProps } from "./types";

const GALLERY_COLS = defaultElementBody("gallery").perRow;

export function GalleryPanel({ element, onChange, path }: ElementPanelProps<"gallery">) {
  const [picking, setPicking] = useState(false);
  const full = element.mediaIds.length >= SITE_LIMITS.galleryImages;
  return (
    <>
      <SortableList<string>
        label="Фото"
        items={element.mediaIds}
        max={SITE_LIMITS.galleryImages}
        onChange={(mediaIds) => onChange({ ...element, mediaIds })}
        summary={(id, i) => (
          <span className="flex items-center gap-2">
            <img src={contentMediaUrl(id)} alt="" className="size-9 rounded object-cover" />
            Фото {i + 1}
          </span>
        )}
      />
      {full ? null : (
        <Button variant="outline" className="self-start" onClick={() => setPicking(true)}>
          <Plus aria-hidden />
          Добавить фото
        </Button>
      )}
      <GridCountField kind="gallery" field="perRow" values={GRID_PER_ROW} value={element.perRow} fallback={GALLERY_COLS} path={path} onChange={(perRow) => onChange({ ...element, perRow: perRow ?? GALLERY_COLS })} />
      <PhoneGridField ariaLabel="Галерея на телефоне" value={element.phoneLayout} onChange={(phoneLayout) => onChange({ ...element, phoneLayout })} />
      <MediaPickerDialog
        open={picking}
        title="Фото в галерею"
        onClose={() => setPicking(false)}
        onSelect={(media) => {
          setPicking(false);
          onChange({ ...element, mediaIds: [...element.mediaIds, media.id] });
        }}
      />
    </>
  );
}

export function VideoPanel({ element, onChange, path, fresh, onFreshDone }: ElementPanelProps<"video">) {
  const { issueAt } = useSectionForm();
  return (
    <>
      <MediaIdField
        label="Видео из медиатеки"
        kind="video"
        required
        value={element.mediaId || null}
        openOnMount={fresh}
        onOpened={onFreshDone}
        error={element.mediaId ? null : (issueAt(`${path}.mediaId`) ?? "Выберите файл видео — без него блок не сохранится.")}
        hint="MP4 или WebM. Плеер загрузится по нажатию — страница не станет тяжелее."
        onChange={(mediaId) => onChange({ ...element, mediaId: mediaId ?? "" })}
      />
      <MediaIdField label="Заставка" value={element.posterMediaId} hint="Кадр до нажатия «Смотреть». Пусто — первый кадр видео." onChange={(posterMediaId) => onChange({ ...element, posterMediaId })} />
    </>
  );
}

export function AudioPanel({ element, onChange, path, fresh, onFreshDone }: ElementPanelProps<"audio">) {
  const { issueAt } = useSectionForm();
  return (
    <>
      <MediaIdField
        label="Звук из медиатеки"
        kind="audio"
        required
        value={element.mediaId || null}
        openOnMount={fresh}
        onOpened={onFreshDone}
        error={element.mediaId ? null : (issueAt(`${path}.mediaId`) ?? "Выберите звуковой файл — без него блок не сохранится.")}
        onChange={(mediaId) => onChange({ ...element, mediaId: mediaId ?? "" })}
      />
      <SectionTextField label="Название" path={`${path}.title`} value={element.title} max={SECTION_TEXT_LIMITS.audioTitle} onChange={(title) => onChange({ ...element, title: title || undefined })} />
    </>
  );
}
